import { gsap } from 'gsap';
import { Container, FillGradient, Graphics, Text } from 'pixi.js';

export type HPBarOptions = {
  width: number;
  height: number;
  maxHP: number;
  name?: string;
  alignRight?: boolean;
  textResolution?: number;
  fontFamily?: string;
};

// Warm 1930s poster palette - sits with the cream name label (0xFFEABF) and
// the ink outline rather than a cold modern red.
const COLOR_INK = 0x241612; // outline - very dark warm brown, not pure black
const COLOR_TRACK = 0x3a2a20; // empty part - aged parchment-dark
// Vertical body gradient: lit warm rim at the top down to deep oxblood -
// reads as a convex tube.
const FILL_RIM = 0xf2a07a;
const FILL_BRIGHT = 0xe4623c;
const FILL_BASE = 0xc23222;
const FILL_SHADE = 0x8e2016;
const FILL_DEEP = 0x5e1410;
// Outline thickness; the fill is inset by this so the ink reads as a frame.
const BORDER = 1;
// gsap-seconds for the fill to glide to a new HP value (scaled by the global
// timeline pace like the rest of the battle, same as FloatingText).
const HP_TWEEN_SEC = 0.16;

export class HPBar extends Container {
  private readonly frame: Graphics;
  private readonly fill: Graphics;
  private readonly gloss: Graphics;
  private readonly fillLayer: Container;
  private readonly fillMask: Graphics;
  private readonly fillGradient: FillGradient;
  private readonly glossGradient: FillGradient;
  private readonly innerX: number;
  private readonly innerY: number;
  private readonly innerW: number;
  private readonly innerH: number;
  private readonly barWidth: number;
  private readonly maxHP: number;
  private readonly alignRight: boolean;
  // Authoritative target HP vs the value currently drawn (animated toward it).
  private currentHP: number;
  private displayedHP: number;
  private fillTween?: gsap.core.Tween;

  constructor(opts: HPBarOptions) {
    super();
    this.barWidth = opts.width;
    this.maxHP = opts.maxHP;
    this.currentHP = opts.maxHP;
    this.displayedHP = opts.maxHP;
    this.alignRight = opts.alignRight ?? false;

    const h = opts.height;
    const outerR = h / 2;
    this.innerX = BORDER;
    this.innerY = BORDER;
    this.innerW = this.barWidth - BORDER * 2;
    this.innerH = h - BORDER * 2;
    const innerR = this.innerH / 2;

    // Rounded track + ink outline in one shape (the empty part of the bar).
    this.frame = new Graphics()
      .roundRect(0, 0, this.barWidth, h, outerR)
      .fill(COLOR_TRACK)
      .stroke({ color: COLOR_INK, width: BORDER });
    this.addChild(this.frame);

    // One gradient instance each, reused every redraw (recreating per frame
    // leaks textures - see FillGradient docs). textureSpace 'local' maps a
    // gradient to the filled rect's own 0-1 box so the shading stays put as
    // the bar drains.
    this.fillGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: FILL_RIM },
        { offset: 0.1, color: FILL_BRIGHT },
        { offset: 0.45, color: FILL_BASE },
        { offset: 0.8, color: FILL_SHADE },
        { offset: 1, color: FILL_DEEP },
      ],
      textureSpace: 'local',
    });
    // Warm specular sheen over the top of the fill - sells the convex tube.
    this.glossGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: 'rgba(255,242,222,0.45)' },
        { offset: 1, color: 'rgba(255,242,222,0)' },
      ],
      textureSpace: 'local',
    });

    // fill + gloss live in a layer clipped by a rounded-rect mask: rounded
    // ends, straight drain edge (the Cuphead-style look).
    this.fill = new Graphics();
    this.gloss = new Graphics();
    this.fillLayer = new Container();
    this.fillLayer.addChild(this.fill, this.gloss);

    this.fillMask = new Graphics().roundRect(
      this.innerX,
      this.innerY,
      this.innerW,
      this.innerH,
      innerR,
    ).fill(0xffffff);
    this.fillLayer.mask = this.fillMask;
    this.addChild(this.fillMask, this.fillLayer);

    if (opts.name) {
      const label = new Text({
        text: opts.name,
        style: {
          fontFamily: opts.fontFamily
            ? `${opts.fontFamily}, system-ui, sans-serif`
            : 'system-ui, sans-serif',
          fontSize: 8,
          fill: 0xFFEABF,
          fontWeight: 'bold',
          stroke: { color: 0x000000, width: 2 },
          letterSpacing: 2.5,
          dropShadow: {
            color: 0x000000,
            alpha: 1,
            angle: Math.PI / 4,
            distance: 2,
            blur: 0,
          },
        },
        resolution: opts.textResolution ?? 1,
      });
      label.y = -18;
      if (this.alignRight) {
        label.anchor.set(1, 0);
        label.x = this.barWidth;
      } else {
        label.x = 0;
      }
      this.addChild(label);
    }

    this.redrawFill();
  }

  // Glides the bar to the new value (both drain and heal animate). Killing the
  // previous tween first lets rapid hits chain smoothly from the drawn value.
  setCurrentHP(current: number): void {
    this.currentHP = Math.max(0, Math.min(this.maxHP, current));
    this.fillTween?.kill();
    const proxy = { v: this.displayedHP };
    this.fillTween = gsap.to(proxy, {
      v: this.currentHP,
      duration: HP_TWEEN_SEC,
      ease: 'power2.out',
      onUpdate: () => {
        this.displayedHP = proxy.v;
        this.redrawFill();
      },
    });
  }

  private redrawFill(): void {
    const ratio = this.maxHP > 0 ? this.displayedHP / this.maxHP : 0;
    const fillW = this.innerW * ratio;
    this.fill.clear();
    this.gloss.clear();
    if (fillW <= 0) return;
    const x = this.alignRight ? this.innerX + this.innerW - fillW : this.innerX;
    this.fill.rect(x, this.innerY, fillW, this.innerH).fill(this.fillGradient);
    // Sheen covers the top ~half of the body.
    this.gloss
      .rect(x, this.innerY, fillW, this.innerH * 0.5)
      .fill(this.glossGradient);
  }
}
